(function () {
  "use strict";

  const VERSION = "0.4.1";

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // The currency this shopper checks out in. Under Markets it differs from
  // the currency rewards are denominated in for every non-primary market.
  function checkoutCurrency() {
    return window.QB?.shop?.checkoutCurrency || window.QB?.shop?.currency || "";
  }

  // True when the reward is denominated in a currency the shopper isn't
  // shopping in — i.e. what they see at checkout will be a converted, smaller
  // number. Drives the unambiguous formatting and the conversion note below.
  function isForeignReward(currency) {
    const checkout = checkoutCurrency();
    return Boolean(currency && checkout && currency !== checkout);
  }

  // Money formatting has to survive Markets. `Intl` picks a symbol from the
  // BROWSER locale, so an NZD reward renders as a bare "$10.00" to anyone
  // whose locale is en-NZ and as "NZ$10.00" to everyone else — meaning an
  // Australian shopper could be shown "$10.00" for a reward that takes about
  // A$8.34 off. When the reward isn't in the shopper's own currency we force
  // the ISO code instead of trusting a symbol. Once rewards are issued in the
  // shopper's own currency the two match and the symbol comes back.
  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
        ...(isForeignReward(currency) ? { currencyDisplay: "code" } : {}),
      }).format(amount);
    } catch {
      return `${currency ? currency + " " : "$"}${Number(amount).toFixed(2)}`;
    }
  }

  function formatDate(iso) {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));
    } catch {
      return iso;
    }
  }

  function getPointsConfig() {
    const config = window.QB?.pointsConfig || {};
    return {
      earnRate: config.earnRate ?? 1,
      redeemRate: config.redeemRate ?? 10,
      minimumRedeem: config.minimumRedeem ?? 100,
      redeemOptions: Array.isArray(config.redeemOptions) && config.redeemOptions.length
        ? config.redeemOptions
        : [config.minimumRedeem ?? 100],
      tiers: Array.isArray(config.tiers) ? config.tiers : [],
      bonusRules: Array.isArray(config.bonusRules) ? config.bonusRules : [],
      voucherExpiryDays: config.voucherExpiryDays ?? null,
      // Reward denomination, server-owned, most specific first:
      //   rewardCurrency  — what THIS customer's reward will actually be in
      //                     (/status resolves it per customer; store credit is
      //                     issued in their currency, not the program's)
      //   config.currency — the program denomination, right for discount codes
      //   shop.currency   — first paint, before /status answers
      // Never infer it from the shopper's market: on the discount-code path
      // the reward genuinely is in shop currency and guessing would lie.
      currency:
        window.QB?.rewardCurrency ?? config.currency ?? window.QB?.shop?.currency ?? "USD",
    };
  }

  // Publish the token the rewards slot already holds so drawer telemetry can
  // attribute a row to a customer. Nothing new is minted, and /track is the
  // only extra consumer — it treats a missing token as anonymous, which is a
  // real data point rather than a gap.
  function shareToken(token) {
    if (window.QB) window.QB.authToken = token;
    return token;
  }

  async function fetchAuthToken(workerUrl, customer) {
    // Shared session token (qb-auth.js) — one mint per session for every slot.
    if (window.QB && typeof window.QB.token === "function") {
      const shared = await window.QB.token(customer);
      if (!shared) throw new Error("auth_failed");
      return shareToken(shared);
    }
    const res = await fetch(`${workerUrl}/auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "token",
        customerId: customer.id,
        email: customer.email,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.token) throw new Error(data.error || "auth_failed");
    return shareToken(data.token);
  }

  async function fetchStatus(workerUrl, customerId, token) {
    // checkoutCurrency = the currency the storefront actually put this
    // shopper in. The worker needs it to say what their reward will be worth
    // — store credit is issued per currency and is invisible at a checkout
    // in any other. Whitelisted server-side against real market currencies.
    const cur = checkoutCurrency();
    const qs = cur ? `&checkoutCurrency=${encodeURIComponent(cur)}` : "";
    const res = await fetch(`${workerUrl}/status?customerId=${encodeURIComponent(customerId)}${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "status_failed");
    return data;
  }

  async function postRedeem(workerUrl, customer, token, points) {
    const res = await fetch(`${workerUrl}/redeem`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      // email rides along for Klaviyo event keying; the device token lets the
      // worker attach dossier fbp/fbc to the LoyaltyRedeem ad event. The
      // worker's token check still gates the redeem itself.
      body: JSON.stringify({
        customerId: customer.id,
        points,
        email: customer.email,
        // Which currency to issue the credit in — see fetchStatus.
        checkoutCurrency: checkoutCurrency() || undefined,
        token: (function () {
          try {
            return localStorage.getItem("qb_device_token") || undefined;
          } catch (e) {
            return undefined;
          }
        })(),
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "redeem_failed");
    return data;
  }

  // Mirrors the Worker's getDerivedRewards — used before /status responds and
  // as the offline fallback (metafield-injected balance only).
  function computeDerived(balance, lifetime, config) {
    const minimum = config.minimumRedeem;
    const points = balance ?? 0;
    const life = lifetime ?? 0;

    const tiers = [...config.tiers].sort((a, b) => a.threshold - b.threshold);
    let tier = tiers.length
      ? tiers[0]
      : { key: "member", name: "Member", threshold: 0, earnMultiplier: 1 };
    for (const t of tiers) {
      if (life >= t.threshold) tier = t;
    }
    const nextTierRaw = tiers.find((t) => t.threshold > life) ?? null;
    const tierSpan = nextTierRaw ? nextTierRaw.threshold - tier.threshold : 0;

    const options = [...new Set(
      config.redeemOptions
        .map((p) => parseInt(p, 10))
        .filter((p) => Number.isFinite(p) && p >= minimum)
    )].sort((a, b) => a - b);
    if (!options.length) options.push(minimum);

    return {
      discountAvailable: points / config.redeemRate,
      pointsToNextReward: Math.max(0, minimum - points),
      progressPercent: minimum > 0 ? Math.min(100, Math.floor((points / minimum) * 100)) : 0,
      canRedeem: points >= minimum,
      minimumRedeem: minimum,
      redeemDiscountValue: minimum / config.redeemRate,
      redeemOptions: options.map((p) => ({
        points: p,
        value: p / config.redeemRate,
        affordable: points >= p,
      })),
      tier: { key: tier.key, name: tier.name, earnMultiplier: tier.earnMultiplier ?? 1 },
      nextTier: nextTierRaw
        ? {
            key: nextTierRaw.key,
            name: nextTierRaw.name,
            threshold: nextTierRaw.threshold,
            earnMultiplier: nextTierRaw.earnMultiplier ?? 1,
            pointsToNext: Math.max(0, nextTierRaw.threshold - life),
            progressPercent:
              tierSpan > 0
                ? Math.min(100, Math.floor(((life - tier.threshold) / tierSpan) * 100))
                : 0,
          }
        : null,
    };
  }

  // Copy with visual feedback on the triggering button. Clipboard API needs a
  // secure context (storefronts are); execCommand is the embedded-webview
  // fallback where navigator.clipboard is unavailable.
  function copyToClipboard(text, btn) {
    const done = () => {
      const label = btn.textContent;
      btn.textContent = "Copied ✓";
      btn.disabled = true;
      setTimeout(() => {
        btn.textContent = label;
        btn.disabled = false;
      }, 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => legacyCopy(text) && done());
    } else if (legacyCopy(text)) {
      done();
    }
  }

  function legacyCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  }

  function formatMultiplier(multiplier) {
    const n = Number(multiplier) || 1;
    return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}×`;
  }

  function buildWaysToEarnHtml(config, tier, currency) {
    const ways = [];
    ways.push(`Earn ${config.earnRate} point${config.earnRate === 1 ? "" : "s"} per ${formatMoney(1, currency)} spent`);
    if (tier && (Number(tier.earnMultiplier) || 1) > 1) {
      ways.push(`${escapeHtml(tier.name)} members earn ${formatMultiplier(tier.earnMultiplier)} points on every order`);
    }
    for (const rule of config.bonusRules) {
      if (rule.label) ways.push(escapeHtml(rule.label));
    }

    return `
      <div class="qb-rewards__ways">
        <p class="qb-rewards__section-title">Ways to earn</p>
        <ul class="qb-rewards__ways-list">
          ${ways.map((w) => `<li>${w}</li>`).join("")}
        </ul>
      </div>
    `;
  }

  function buildTierProgressHtml(derived) {
    const tier = derived?.tier;
    if (!tier) return "";
    const next = derived.nextTier;

    if (!next) {
      return `
        <div class="qb-rewards__tier-progress">
          <p class="qb-rewards__section-title">Membership</p>
          <p class="qb-rewards__tier-note">You're a ${escapeHtml(tier.name)} — our top tier${
            (Number(tier.earnMultiplier) || 1) > 1
              ? `, earning ${formatMultiplier(tier.earnMultiplier)} points on every order`
              : ""
          }.</p>
        </div>
      `;
    }

    return `
      <div class="qb-rewards__tier-progress">
        <div class="qb-rewards__progress-header">
          <span>${next.pointsToNext} pts to ${escapeHtml(next.name)}</span>
          <span>${formatMultiplier(next.earnMultiplier)} points</span>
        </div>
        <div class="qb-rewards__progress-track qb-rewards__progress-track--tier" role="progressbar" aria-valuenow="${next.progressPercent}" aria-valuemin="0" aria-valuemax="100" aria-label="Progress to ${escapeHtml(next.name)} tier">
          <div class="qb-rewards__progress-fill" style="width: ${next.progressPercent}%"></div>
        </div>
        <p class="qb-rewards__tier-note">Reach ${escapeHtml(next.name)} to earn ${formatMultiplier(next.earnMultiplier)} points on every order.</p>
      </div>
    `;
  }

  function buildRedeemHtml(derived, currency, loading) {
    const options = derived?.redeemOptions ?? [];

    if (options.length > 1) {
      return `
        <div class="qb-rewards__redeem">
          <p class="qb-rewards__section-title">Redeem points</p>
          <div class="qb-rewards__options">
            ${options
              .map(
                (o) => `
              <button
                class="qb-rewards__option"
                type="button"
                data-qb-redeem="${o.points}"
                ${!o.affordable || loading ? "disabled" : ""}
              >
                <span class="qb-rewards__option-value">${formatMoney(o.value, currency)}</span>
                <span class="qb-rewards__option-points">${o.points} pts</span>
              </button>
            `
              )
              .join("")}
          </div>
          ${loading ? `<p class="qb-rewards__meta" role="status">Processing…</p>` : ""}
        </div>
      `;
    }

    const single = options[0] ?? { points: derived?.minimumRedeem ?? 100, value: derived?.redeemDiscountValue ?? 10, affordable: derived?.canRedeem ?? false };
    return `
      <button
        class="qb-rewards__btn"
        type="button"
        data-qb-redeem="${single.points}"
        ${!single.affordable || loading ? "disabled" : ""}
      >
        ${loading ? "Processing…" : single.affordable ? `Redeem ${formatMoney(single.value, currency)} off` : `Need ${derived?.pointsToNextReward ?? single.points} more pts`}
      </button>
    `;
  }

  function buildVouchersHtml(vouchers, currency) {
    // A store-credit reward has no code, so "has a code" can't be the test
    // for "is a real reward" any more — a value is.
    const active = (vouchers || []).filter(
      (v) => v && (v.code || v.value != null) && !v.used && !v.expired
    );
    if (!active.length) return "";

    const anyCoded = active.some((v) => v.code);
    return `
      <div class="qb-rewards__vouchers">
        <p class="qb-rewards__section-title">Your rewards</p>
        ${active
          .map((v) => {
            const detail = v.expiresAt
              ? `expires ${formatDate(v.expiresAt)}`
              : formatDate(v.createdAt);
            // Each reward carries the currency it was issued in — a customer
            // can hold rewards from before and after a currency change.
            const money = v.value != null ? formatMoney(v.value, v.currency || currency) : null;
            const label = v.code
              ? escapeHtml(v.code)
              : money
                ? `${escapeHtml(money)} credit`
                : "Reward";
            const meta = v.code
              ? `${money ? `${money} off` : "Reward"}${detail ? ` · ${escapeHtml(detail)}` : ""}`
              : `On your account${detail ? ` · ${escapeHtml(detail)}` : ""}`;
            return `
          <div class="qb-rewards__voucher">
            <div class="qb-rewards__voucher-info">
              <span class="qb-rewards__voucher-code">${label}</span>
              <span class="qb-rewards__voucher-meta">${meta}</span>
            </div>
            ${v.code ? `<button class="qb-rewards__voucher-copy" type="button" data-qb-copy="${escapeHtml(v.code)}">Copy</button>` : ""}
          </div>
        `;
          })
          .join("")}
        ${/* Whether Shopify pre-selects store credit at checkout is NOT
             reliable: observed unticked on a first redemption and ticked on
             the second (2026-08-11). So the copy must not promise either —
             "applies automatically" strands anyone who gets the unticked
             variant, and "you must select it" reads as broken when it's
             already on. Tell them to look for it and confirm. */ ""}
        <p class="qb-rewards__fine">${anyCoded ? "Single use, only works on your account." : "Sign in at checkout and check store credit is selected before you pay."}</p>
      </div>
    `;
  }

  function buildGuestHtml(config, currency) {
    const tiers = [...config.tiers].sort((a, b) => a.threshold - b.threshold);
    const tierRows = tiers.length > 1
      ? `
        <div class="qb-rewards__tier-ladder">
          ${tiers
            .map(
              (t) => `
            <div class="qb-rewards__tier-row">
              <span class="qb-rewards__tier-row-name">${escapeHtml(t.name)}</span>
              <span class="qb-rewards__tier-row-detail">${t.threshold > 0 ? `${t.threshold}+ pts` : "Everyone"} · ${formatMultiplier(t.earnMultiplier)} points</span>
            </div>
          `
            )
            .join("")}
        </div>
      `
      : "";

    return `
      <div class="qb-rewards qb-rewards--guest">
        <p class="qb-rewards__message">Earn ${config.earnRate} point${config.earnRate === 1 ? "" : "s"} for every ${formatMoney(1, currency)} you spend. Redeem ${config.minimumRedeem} points for ${formatMoney(config.minimumRedeem / config.redeemRate, currency)} off.</p>
        ${tierRows}
        <a class="qb-rewards__btn" href="${escapeHtml(window.QB?.loginUrl?.("rewards") || "/account/login")}">Sign in</a>
        <a class="qb-rewards__link" href="${escapeHtml(window.QB?.registerUrl?.("rewards") || "/account/register")}">Create account</a>
      </div>
    `;
  }

  function buildRewardsHtml(state) {
    const { loyalty, derived, currency, loading, error, redeemResult } = state;
    const config = getPointsConfig();
    const tierName = escapeHtml(derived?.tier?.name || loyalty.tier || "Member");
    const points = loyalty.points ?? 0;
    const progress = derived?.progressPercent ?? 0;
    const discountAvailable = derived?.discountAvailable ?? 0;
    const pointsToNext = derived?.pointsToNextReward ?? 0;
    const canRedeemNow = derived?.canRedeem ?? false;
    const enrolled = formatDate(loyalty.enrolledAt);
    const lastEarn = loyalty.lastEarnPoints > 0
      ? `+${loyalty.lastEarnPoints} pts on ${formatDate(loyalty.lastEarnDate) || "your last order"}`
      : null;
    const lastRedeem = formatDate(loyalty.lastRedeemDate);

    if (redeemResult) {
      // The server tells us both what the reward is worth and what currency
      // it's in — never re-derive either here. Store credit has no code to
      // copy: it's already on the account and applies once they're signed in.
      const rewardCurrency = redeemResult.currency || currency;
      const isCredit = redeemResult.mechanism === "store_credit" || !redeemResult.discountCode;
      const amount = formatMoney(redeemResult.discountValue, rewardCurrency);
      const validUntil = redeemResult.expiresAt
        ? ` and is valid until <strong>${escapeHtml(formatDate(redeemResult.expiresAt))}</strong>`
        : "";
      return `
        <div class="qb-rewards qb-rewards--success">
          <p class="qb-rewards__success-title">Your reward is ready</p>
          ${isCredit
            ? `<p class="qb-rewards__code">${escapeHtml(amount)}</p>
          <p class="qb-rewards__message">${amount} of store credit is on your account${validUntil}. Sign in at checkout and you'll see it as a payment option — check it's selected before you pay.</p>`
            : `<p class="qb-rewards__code">${escapeHtml(redeemResult.discountCode)}</p>
          <button class="qb-rewards__btn" type="button" data-qb-copy="${escapeHtml(redeemResult.discountCode)}">Copy code</button>
          <p class="qb-rewards__message">${amount} off your next order — it only works on your account${validUntil}. It's saved under <strong>Your vouchers</strong> until then.</p>`}
          <p class="qb-rewards__balance">New balance: <strong>${redeemResult.pointsBalance} pts</strong></p>
          <button class="qb-rewards__btn qb-rewards__btn--secondary" type="button" data-qb-back>Back to rewards</button>
        </div>
      `;
    }

    return `
      <div class="qb-rewards">
        <div class="qb-rewards__card">
          <div class="qb-rewards__card-header">
            <span class="qb-rewards__tier" data-qb-tier="${escapeHtml(derived?.tier?.key || "member")}">${tierName}</span>
            ${enrolled ? `<span class="qb-rewards__since">Since ${escapeHtml(enrolled)}</span>` : ""}
          </div>
          <p class="qb-rewards__balance-label">Your balance</p>
          <p class="qb-rewards__balance">${points} <span>points</span></p>
          ${loyalty.pointsLifetime > 0 ? `<p class="qb-rewards__lifetime">${loyalty.pointsLifetime} pts earned all time</p>` : ""}
        </div>

        ${buildTierProgressHtml(derived)}

        <div class="qb-rewards__progress">
          <div class="qb-rewards__progress-header">
            <span>${canRedeemNow ? "Ready to redeem" : `${pointsToNext} pts to go`}</span>
            ${/* Below the threshold this used to read "NZ$0.50 available" off a
                 950-points-short balance — a number that is not available and
                 cannot be spent. Saying what the reward IS keeps the goal
                 concrete (and honest); "available" appears only once it is. */ ""}
            <span>${
              canRedeemNow
                ? `${formatMoney(derived?.redeemDiscountValue ?? discountAvailable, currency)} available`
                : `${formatMoney(derived?.redeemDiscountValue ?? 0, currency)} at ${derived?.minimumRedeem ?? 0} pts`
            }</span>
          </div>
          <div class="qb-rewards__progress-track" role="progressbar" aria-valuenow="${progress}" aria-valuemin="0" aria-valuemax="100" aria-label="Progress to next reward">
            <div class="qb-rewards__progress-fill" style="width: ${progress}%"></div>
          </div>
        </div>

        ${buildVouchersHtml(state.vouchers, currency)}

        ${lastEarn ? `<p class="qb-rewards__meta">${escapeHtml(lastEarn)}</p>` : ""}
        ${lastRedeem ? `<p class="qb-rewards__meta">Last redeemed ${escapeHtml(lastRedeem)}</p>` : ""}

        ${error ? `<p class="qb-rewards__error" role="alert">${escapeHtml(error)}</p>` : ""}

        ${buildRedeemHtml(derived, currency, loading)}

        ${buildWaysToEarnHtml(config, derived?.tier, currency)}

        <p class="qb-rewards__fine">Redeem ${derived?.minimumRedeem ?? 100} points for ${formatMoney(derived?.redeemDiscountValue ?? 10, currency)} off.${config.voucherExpiryDays ? ` Vouchers are valid for ${config.voucherExpiryDays} days.` : ""}${isForeignReward(currency) ? ` Rewards are issued in ${escapeHtml(currency)} — the exact amount off in ${escapeHtml(checkoutCurrency())} is shown at checkout.` : ""}</p>
      </div>
    `;
  }

  function defaultLoyaltyFromCustomer(customer) {
    return {
      points: customer.points ?? 0,
      pointsLifetime: customer.pointsLifetime ?? 0,
      pointsRedeemedLifetime: customer.pointsRedeemedLifetime ?? 0,
      tier: customer.tier ?? "Member",
      enrolledAt: customer.enrolledAt ?? null,
      lastEarnDate: customer.lastEarnDate ?? null,
      lastRedeemDate: customer.lastRedeemDate ?? null,
      lastEarnPoints: customer.lastEarnPoints ?? 0,
      programStatus: customer.programStatus ?? "active",
    };
  }

  class QBRewardsSlot {
    constructor(panel) {
      this.panel = panel;
      this.state = {
        loyalty: {},
        derived: {},
        vouchers: [],
        // The currency REWARDS are denominated in — not the shopper's. The
        // server sends it on /status (pointsConfig.currency); the Liquid seed
        // covers first paint.
        currency: window.QB?.pointsConfig?.currency ?? window.QB?.shop?.currency ?? "USD",
        loading: false,
        error: null,
        redeemResult: null,
        token: null,
      };
      this.bind();
      this.load();
    }

    bind() {
      this.panel.addEventListener("click", async (e) => {
        const copy = e.target.closest("[data-qb-copy]");
        if (copy) {
          copyToClipboard(copy.dataset.qbCopy, copy);
          return;
        }

        const back = e.target.closest("[data-qb-back]");
        if (back) {
          this.state.redeemResult = null;
          this.render();
          return;
        }

        const btn = e.target.closest("[data-qb-redeem]");
        if (!btn || btn.disabled) return;
        const points = parseInt(btn.dataset.qbRedeem, 10);
        await this.redeem(Number.isFinite(points) ? points : undefined);
      });
    }

    render() {
      // Re-read every paint: /config and /status both replace
      // window.QB.pointsConfig after first paint, and the reward currency
      // rides on it. Holding it in state would strand the seeded value.
      this.state.currency = getPointsConfig().currency;
      const customer = window.QB?.customer;
      if (!customer?.loggedIn) {
        this.panel.innerHTML = buildGuestHtml(getPointsConfig(), this.state.currency);
        return;
      }
      this.panel.innerHTML = buildRewardsHtml(this.state);
    }

    async load() {
      const customer = window.QB?.customer;
      if (!customer?.loggedIn) {
        this.render();
        // Guests still get the real program config (tier ladder, rates) —
        // /config is public and cached.
        const workerUrl = window.QB?.worker?.url;
        if (workerUrl) {
          try {
            const res = await fetch(`${workerUrl}/config`);
            const data = await res.json();
            if (res.ok && data.pointsConfig) {
              window.QB.pointsConfig = data.pointsConfig;
              this.render();
            }
          } catch {
            /* keep local defaults */
          }
        }
        return;
      }

      this.state.loyalty = defaultLoyaltyFromCustomer(customer);
      this.state.derived = computeDerived(
        this.state.loyalty.points,
        this.state.loyalty.pointsLifetime,
        getPointsConfig()
      );
      this.render();

      const workerUrl = window.QB?.worker?.url;
      if (!workerUrl || !customer.id || !customer.email) return;

      try {
        this.state.token = await fetchAuthToken(workerUrl, customer);
        const status = await fetchStatus(workerUrl, customer.id, this.state.token);
        this.state.loyalty = {
          points: status.points,
          pointsLifetime: status.pointsLifetime,
          pointsRedeemedLifetime: status.pointsRedeemedLifetime,
          tier: status.tier,
          enrolledAt: status.enrolledAt,
          lastEarnDate: status.lastEarnDate,
          lastRedeemDate: status.lastRedeemDate,
          lastEarnPoints: status.lastEarnPoints,
          programStatus: status.programStatus,
        };
        this.state.derived = status.derived;
        this.state.vouchers = Array.isArray(status.vouchers) ? status.vouchers : [];
        if (status.pointsConfig) window.QB.pointsConfig = status.pointsConfig;
        // Per-customer reward denomination — see getPointsConfig(). Only
        // /status knows this; /config is public and customer-agnostic.
        if (status.rewardCurrency) window.QB.rewardCurrency = status.rewardCurrency;
        window.QB.customer.points = status.points;
        this.render();
      } catch (err) {
        this.state.error = err.message === "auth_not_configured"
          ? "Rewards syncing soon — showing cached balance."
          : null;
        this.render();
      }
    }

    async redeem(points) {
      const customer = window.QB?.customer;
      const workerUrl = window.QB?.worker?.url;
      if (!customer?.loggedIn || !workerUrl) return;

      window.QB?.track?.("redeem_click", { slot: "rewards", meta: { points } });

      this.state.loading = true;
      this.state.error = null;
      this.render();

      try {
        if (!this.state.token) {
          this.state.token = await fetchAuthToken(workerUrl, customer);
        }
        const result = await postRedeem(workerUrl, customer, this.state.token, points);
        this.state.redeemResult = result;
        this.state.loyalty.points = result.pointsBalance;
        this.state.derived = result.derived;
        this.state.vouchers = [
          {
            code: result.discountCode,
            value: result.discountValue,
            createdAt: new Date().toISOString(),
            expiresAt: result.expiresAt ?? null,
            used: false,
            expired: false,
          },
          ...this.state.vouchers,
        ];
        window.QB.customer.points = result.pointsBalance;
      } catch (err) {
        const messages = {
          insufficient_balance: "Not enough points to redeem yet.",
          invalid_redeem_option: "That option isn't available — refresh and try again.",
          program_suspended: "Your rewards account is suspended. Contact support.",
          unauthorized: "Session expired — refresh the page and try again.",
          rate_limited: "Too many redemptions — please try again shortly.",
        };
        this.state.error = messages[err.message] || "Could not redeem. Please try again.";
      } finally {
        this.state.loading = false;
        this.render();
      }
    }
  }

  window.QB = window.QB || {};
  window.QB.slotModules = window.QB.slotModules || {};
  window.QB.slotModules.rewards = {
    version: VERSION,
    init(panel) {
      return new QBRewardsSlot(panel);
    },
  };
})();
